import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Toast } from 'primereact/toast';
import { Tooltip } from 'primereact/tooltip';
import { translatedMessage } from '../../../service/LanguageService';
import { CompetitionService } from '../../../service/CompetitionService';
import { ApplicationService } from '../../../service/ApplicationService';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import DataTableUtils from '../../../utilities/DataTableUtils';
import GeneralUtils from '../../../utilities/GeneralUtils';

const ApplicationTestCenterDialog = (props) => {
    const [availableTestCenters, setAvailableTestCenters] = useState([]);
    const [applicationTestSession, setApplicationTestSession] = useState({});
    const [dialogVisible, setDialogVisible] = useState(false);
    const [testSessionDialogVisible, setTestSessionDialogVisible] = useState(false);
    const [testSessionDate, setTestSessionDate] = useState("");

    const toast = useRef(null);
    const dt = useRef(null);

    const competitionService = useMemo(() => new CompetitionService(), []);
    const applicationService = useMemo(() => new ApplicationService(), []);

    useEffect(() => {
        setDialogVisible(props.visible);
    }, [props.visible]);

    useEffect(() => {
        const getAvailableTestCenters = async (competitionId) => {
            if (competitionId) {
                await competitionService
                    .getAvailableTestCenters(competitionId)
                    .then((_testCentersResult) => {
                        setAvailableTestCenters(_testCentersResult);
                    })
                    .catch((error) => toast.current.show({ severity: 'error', summary: translatedMessage(error), life: 5000 }));
            }
        };
        // console.log('ApplicationTestCenterDialog', props.applicationData);
        getAvailableTestCenters(props?.applicationData?.application?.competition?.id);
    }, [competitionService, props.applicationData]);

    const assignTestCenter = async (testCenterData) => {
        // console.log('application', props.applicationData.application);
        // console.log('testCenter', testCenterData);

        await applicationService
            .assignApplicationToTestCenter(props.applicationData.application.id, testCenterData.testCenter.id)
            .then((_applicationTestSession) => {
                toast.current.show({ severity: 'success', summary: translatedMessage('generic.save.success') + '' });
                setApplicationTestSession(_applicationTestSession);
                setTestSessionDialogVisible(true);

                let _testSessionDate = GeneralUtils.formatDate(_applicationTestSession.testSession.startDate) + ", " +
                    GeneralUtils.formatTime(_applicationTestSession.testSession.startDate) + " - " +
                    GeneralUtils.formatTime(_applicationTestSession.testSession.endDate)
                setTestSessionDate(_testSessionDate)

                if (typeof props.onSave === 'function') {
                    props.onSave(_applicationTestSession);
                }
                hideDialog();
            })
            .catch((error) => toast.current.show({ severity: 'error', summary: translatedMessage(error), life: 5000 }));
    };

    const hideDialog = () => {
        props.onCancel();
    };

    const testCenterDialogFooter = (
        <>
            <Button label={translatedMessage('generic.cancel')} icon="pi pi-times" className="p-button-text" onClick={hideDialog} />
        </>
    );

    const prefferedTestCenterTemplate = (testCenterData) => {
        return (
            testCenterData.testCenter.id === props.applicationData.chosenTestCenter?.id
                ? (
                    <>
                        <Tooltip target=".custom-target-icon" />
                        <span style={{ color: 'green' }} className="custom-target-icon mr-1 " data-pr-tooltip={translatedMessage('application.testCenter.preferred')}>
                            <FontAwesomeIcon icon="far fa-star" size="xl" beat />
                        </span>
                    </>
                )
                : ''
        );
    };

    const actionBodyTemplate = (testCenterData) => {
        return (
            <div className="actions flex flex-wrap align-items-center justify-content-end">
                <Button
                    icon="pi pi-check"
                    className="p-button-rounded p-button-info m-1"
                    onClick={() => assignTestCenter(testCenterData)}
                    tooltip={translatedMessage('testSession.application.assignToTestCenter')}
                    tooltipOptions={{ showOnDisabled: true, position: 'top' }}
                    disabled={testCenterData?.testCenter?.id === props.applicationData?.testSession?.competitionTestCenterRoom?.testCenterRoom?.testCenter?.id}
                />
            </div>
        );
    };

    return (
        <>
            <Toast ref={toast} />
            <Dialog
                visible={dialogVisible}
                header={
                    <>
                        {props.applicationData.application?.user?.fullName} - {translatedMessage('testSession.assignToTestCenter')}
                    </>
                }
                modal
                className="pcn-dialog p-fluid"
                footer={testCenterDialogFooter}
                onHide={hideDialog}
            >
                <DataTable
                    ref={dt}
                    value={availableTestCenters}
                    dataKey="id"
                    paginator
                    rows={DataTableUtils.defalRowsPerPage()}
                    rowsPerPageOptions={DataTableUtils.rowsPerPageOptions()}
                    className="datatable-responsive pcn-datatable"
                    responsiveLayout="stack"
                    paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                    currentPageReportTemplate={'{first} - {last} ' + translatedMessage('generic.of') + ' {totalRecords}'}
                    emptyMessage={translatedMessage('generic.tableEmptyMessage')}
                >
                    <Column field="testCenter.county.label" header={translatedMessage('generic.county')} sortable />
                    <Column field="testCenter.city" header={translatedMessage('generic.city')} sortable />
                    <Column field="testCenter.name" header={translatedMessage('competition.testCenter.name')} sortable />
                    <Column body={prefferedTestCenterTemplate} header={translatedMessage('testSession.preferredTestCenter')} />
                    <Column body={actionBodyTemplate}></Column>
                </DataTable>
            </Dialog>
            <Dialog
                visible={testSessionDialogVisible}
                style={{ width: '450px' }}
                header={applicationTestSession.application?.user?.fullName}
                modal
                className="pcn-dialog p-fluid"
                footer={
                    <Button
                        label={translatedMessage('generic.close')}
                        icon="pi pi-times"
                        className="p-button-text"
                        onClick={() => {
                            setTestSessionDialogVisible(false);
                        }}
                    />
                }
                onHide={() => setTestSessionDialogVisible(false)}
            >
                <>
                    <div>
                        {translatedMessage("competition.testSession.distribution.message.session", 
                            applicationTestSession?.application?.fullName, testSessionDate
                        )}
                    </div>
                    <div>
                        {translatedMessage("competition.testSession.distribution.message.testCenter", 
                            applicationTestSession?.testSession?.competitionTestCenterRoom?.testCenterRoom?.testCenter?.name, applicationTestSession?.testSession?.competitionTestCenterRoom?.testCenterRoom?.name
                        )}
                    </div>
                    <div>
                        {translatedMessage("competition.testCenter.address") + ": "}
                        {applicationTestSession?.testSession?.competitionTestCenterRoom?.testCenterRoom?.testCenter?.address + ", "}
                        {applicationTestSession?.testSession?.competitionTestCenterRoom?.testCenterRoom?.testCenter?.city + ", "}
                        {applicationTestSession?.testSession?.competitionTestCenterRoom?.testCenterRoom?.testCenter?.county?.label}    
                    </div>                    
                </>
            </Dialog>
        </>
    );
};

export default ApplicationTestCenterDialog;
